import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { useDialogs } from '../../components/PromptDialog';
import { fetchPost, patchAsset, patchPost, socialKeys } from './api';
import { ChannelBar, CopyField, KuerzelChip, StatusPill, dauer, kanalName, tagKurz } from './bausteine';
import { langfassungText } from './langfassung';
import type { CockpitDaten, LangfassungsStand, Pruefpunkt } from './types';
import { useCanWrite } from '../auth/permissions';
import { PostReviewPanel } from './PostReviewPanel';

const t = darkTheme.colors;

/** Grenzen der Plattformen. Weich = dort wird gekürzt, hart = dort wird abgewiesen. */
const GRENZEN: Record<string, { weich?: number; hart: number }> = {
  'YOUTUBE.title': { weich: 70, hart: 100 },
  'YOUTUBE.description': { hart: 5000 },
  'INSTAGRAM.description': { weich: 125, hart: 2200 },
  // Content Posting API: `post_info.title` höchstens 2.200 UTF-16-Zeichen (CUR-2248).
  'TIKTOK.description': { hart: 2200 },
  'FACEBOOK.description': { hart: 63206 },
};

/**
 * Post-Cockpit (CUR-324) — **einspaltig, und das ist der Punkt.**
 *
 * Der erste Entwurf sah zwei Spalten vor (Vorschau links, Felder rechts). Das
 * ist falsch, sobald man fragt, *wie* der Schirm benutzt wird: Der Hub liegt
 * **neben** dem YouTube-Studio bzw. dem Instagram-Fenster, nicht im Vollbild.
 * Auf einem 1440-px-Schirm bleiben ihm rund 700 px; zwei Spalten wären darin je
 * 340 px — zu schmal für den Beschreibungsblock, der das längste und wichtigste
 * Feld ist.
 *
 * Einspaltig bei 760 px löst drei Dinge auf einmal: es passt neben das
 * Upload-Fenster, es braucht **keinen** eigenen Mobil-Entwurf (dieselbe Spalte,
 * nur schmaler), und die Reihenfolge der Felder bleibt deckungsgleich mit der
 * Upload-Maske — man arbeitet sich von oben nach unten durch beide.
 */
export function PostCockpitPage(): JSX.Element {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const dialogs = useDialogs();
  const [fehler, setFehler] = useState<string | null>(null);
  // Nur-Lesen-Rolle sieht den Termin samt Stand der Pflichten, aendert aber
  // weder Haken noch Status (CUR-995).
  const canWrite = useCanWrite();

  const post = useQuery({ queryKey: socialKeys.post(id), queryFn: () => fetchPost(id), enabled: Boolean(id) });

  const mutation = useMutation({
    mutationFn: (patch: Parameters<typeof patchPost>[1]) => patchPost(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.post(id) });
      void qc.invalidateQueries({ queryKey: ['social', 'calendar'] });
      setFehler(null);
    },
    onError: (e: Error) => setFehler(e.message),
  });

  // Der Ordner hängt am Beitrag, nicht an der Fassung — er gilt für alle Plattformen (CUR-2289).
  const ordnerMutation = useMutation({
    mutationFn: ({ assetId, ordner }: { assetId: string; ordner: string | null }) => patchAsset(assetId, { ordner }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.post(id) });
      void qc.invalidateQueries({ queryKey: ['social', 'calendar'] });
      setFehler(null);
    },
    onError: (e: Error) => setFehler(e.message),
  });

  if (post.isLoading) return <Skelett />;
  if (post.isError || !post.data) {
    return (
      <div className="mx-auto max-w-[760px] p-6">
        <p style={{ color: t.danger }}>Dieser Termin ließ sich nicht laden.</p>
        <Link to="/social" style={{ color: t.accentLight }}>‹ Kalender</Link>
      </div>
    );
  }

  const d = post.data;
  const v = d.variant;
  const pflichten: Pruefpunkt[] = v?.checklist ?? [];
  const offeneBlocker = pflichten.filter((p) => p.blocking && !d.checkedItems.includes(p.id));
  const erledigt = pflichten.filter((p) => d.checkedItems.includes(p.id)).length;

  const hakenUmschalten = (pid: string): void => {
    const naechste = d.checkedItems.includes(pid)
      ? d.checkedItems.filter((x) => x !== pid)
      : [...d.checkedItems, pid];
    mutation.mutate({ checkedItems: naechste });
  };

  const alsGepostet = async (): Promise<void> => {
    if (d.reviewRequired && d.reviewBlockReason) {
      setFehler(d.reviewBlockReason);
      return;
    }
    // **Warnen, nicht sperren.** Der Hub weiß nie, was in der fremden
    // Upload-Maske wirklich geklickt wurde. Vielleicht ist der Haken gesetzt
    // und nur nicht abgehakt — das kann er nicht wissen, also darf er mahnen
    // und muss durchlassen.
    if (offeneBlocker.length > 0) {
      const ok = await dialogs.confirm({
        title: 'Pflicht noch offen',
        message:
          `${offeneBlocker.map((p) => `• ${p.text}`).join('\n\n')}\n\n` +
          'Ohne diese Punkte fehlt bei dieser Folge die Offenlegung — im Bild steht bewusst keine. ' +
          'Trotzdem als gepostet markieren?',
        confirmLabel: 'Trotzdem markieren',
        destructive: true,
      });
      if (!ok) return;
    }
    const url = await dialogs.prompt({
      title: 'Als gepostet markieren',
      label: 'Live-URL',
      placeholder: d.channel === 'INSTAGRAM' ? 'https://www.instagram.com/reel/…'
        : d.channel === 'TIKTOK' ? 'https://www.tiktok.com/@currivium/video/…'
        : d.channel === 'FACEBOOK' ? 'https://www.facebook.com/…' : 'https://youtube.com/shorts/…',
      message: 'Damit später niemand rätselt, wo die Folge gelandet ist.',
      confirmLabel: 'Markieren',
    });
    if (url === null) return;
    mutation.mutate({ status: 'GEPOSTET', ...(url ? { postedUrl: url } : {}) });
  };

  const grenze = (feld: 'title' | 'description') => GRENZEN[`${d.channel}.${feld}`];

  return (
    <div className="mx-auto max-w-[760px] p-4 pb-28 lg:p-6 lg:pb-28">
      <Link to="/social" className="mb-3 inline-block text-sm" style={{ color: t.accentLight }}>‹ Kalender</Link>

      <header className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Das Kürzel groß vor dem Titel — es ist der Name, unter dem die Folge im Repo liegt (CUR-2287). */}
          <h1 className="flex flex-wrap items-center gap-3 text-xl font-bold" style={{ color: t.textPrimary }}>
            <KuerzelChip kuerzel={d.asset.kuerzel} gross />
            <span>{d.title ?? d.asset.title}</span>
          </h1>
          <StatusPill status={d.status} />
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm" style={{ color: t.textTertiary }}>
          <ChannelBar channel={d.channel} />
          <span style={{ color: d.overdue ? t.warning : t.textTertiary }}>
            {d.date ? tagKurz(d.date) : 'ohne Termin'}{d.overdue ? ' · Termin vorbei' : ''}
          </span>
          <span>{d.asset.aspectRatio ?? ''} · {dauer(d.asset.durationSec)}</span>
        </p>
        <Ordner ordner={d.asset.ordner} ausManifest={d.asset.ausManifest} aenderbar={canWrite && !d.asset.ausManifest}
          speichert={ordnerMutation.isPending} verwerfen={() => setFehler(null)}
          speichern={(ordner) => ordnerMutation.mutateAsync({ assetId: d.asset.id, ordner })} />
      </header>

      {fehler && (
        <p className="mb-4 rounded-md border p-3 text-sm" style={{ borderColor: t.danger, color: t.danger }}>{fehler}</p>
      )}

      <Vorschau daten={d} />

      {/* Auch ohne Christine (K2): Titel, Text und Medien gehören bearbeitbar ins Cockpit, nur ohne Freigabe-Teil. */}
      {(d.reviewRequired || d.reviewVerzicht) && <PostReviewPanel post={d} canWrite={canWrite}
        refresh={() => { void qc.invalidateQueries({ queryKey: socialKeys.post(id) }); void qc.invalidateQueries({ queryKey: ['social', 'calendar'] }); }}
        confirm={dialogs.confirm} />}

      {!d.reviewRequired && d.reviewVerzicht && <OhneChristine verzicht={d.reviewVerzicht} langfassung={d.langfassung} />}

      {v?.nichtImManifestSeit && (
        <p className="mb-4 rounded-md border p-3 text-sm" style={{ borderColor: t.warning, color: t.warning }}>
          {kanalName(d.channel)} steht seit {new Date(v.nichtImManifestSeit).toLocaleDateString('de-DE')} nicht mehr im
          Manifest. Dieser Termin bleibt, einen neuen auf diesem Kanal gibt es nicht.
        </p>
      )}

      {!v && (
        <p className="mb-4 rounded-md border p-3 text-sm" style={{ borderColor: t.warning, color: t.warning }}>
          Für {d.channel} gibt es keine Fassung in diesem Asset — der Termin ist nicht postbar.
          Erst das Manifest ergänzen und neu einspielen.
        </p>
      )}

      {v && (
        <>
          {/* Der Titel der Fassung, nicht der Variante — die überschreibt jeder Sync (CUR-2249). */}
          {d.title && (
            <CopyField label="Titel" value={d.title} limitWeich={grenze('title')?.weich} limitHart={grenze('title')?.hart} />
          )}
          {v.description && (
            <CopyField
              label={d.channel === 'YOUTUBE' ? 'Beschreibung' : 'Caption'}
              value={d.caption ?? v.description}
              limitWeich={grenze('description')?.weich}
              limitHart={grenze('description')?.hart}
              mono
              scrollbar
            />
          )}
          {/* Die Tags der Fassung, nicht der Variante — wie beim Titel (CUR-2295). Nur YouTube kennt Tags. */}
          {d.channel === 'YOUTUBE' && d.tags.length > 0 && <CopyField label="Tags" value={d.tags.join(', ')} />}
          {v.hashtags.length > 0 && <CopyField label="Hashtags" value={v.hashtags.join(' ')} />}
          {v.altText && <CopyField label="Alt-Text" value={v.altText} scrollbar />}
          {v.chapters && <CopyField label="Kapitelmarken" value={v.chapters} mono />}

          {v.settings && v.settings.length > 0 && <Einstellungen settings={v.settings} />}
          {v.notes && Object.keys(v.notes).length > 0 && <Notizen notes={v.notes} />}

          <Pflichten
            pflichten={pflichten}
            gehakt={d.checkedItems}
            erledigt={erledigt}
            offeneBlocker={offeneBlocker.length}
            umschalten={hakenUmschalten}
            aenderbar={canWrite}
          />
        </>
      )}

      {d.postedUrl && (
        <p className="mb-4 text-sm">
          <span style={{ color: t.textTertiary }}>Live: </span>
          <a href={d.postedUrl} target="_blank" rel="noreferrer" style={{ color: t.accentLight }}>{d.postedUrl}</a>
        </p>
      )}

      {/* Klebt unten, damit sie erreichbar bleibt, ohne durch die Beschreibung
          zurückscrollen zu müssen. */}
      <div className="fixed inset-x-0 bottom-0 border-t p-3"
        style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
        <div className="mx-auto flex max-w-[760px] flex-wrap items-center justify-end gap-2">
          {/* Eine App-Fassung ist Fachreview, kein Post — der Server weist es ohnehin ab (CUR-2247). */}
          {canWrite && d.channel !== 'APP' &&
            (d.status !== 'GEPOSTET' ? (
              <button type="button" onClick={() => void alsGepostet()} disabled={mutation.isPending || Boolean(d.reviewRequired && d.reviewBlockReason)}
                className="min-h-[44px] rounded-lg px-4 font-semibold text-white"
                style={{ backgroundColor: t.accent, opacity: mutation.isPending || (d.reviewRequired && d.reviewBlockReason) ? 0.6 : 1 }}>
                Als gepostet markieren
              </button>
            ) : (
              <button type="button" onClick={() => mutation.mutate({ status: 'GEPLANT' })}
                className="min-h-[44px] rounded-lg px-4 text-sm font-semibold"
                style={{ backgroundColor: t.bgElevated, color: t.textSecondary }}>
                Doch nicht gepostet
              </button>
            ))}
          <button type="button" onClick={() => navigate('/social')}
            className="min-h-[44px] rounded-lg px-4 text-sm" style={{ color: t.textTertiary }}>
            Zurück
          </button>
        </div>
      </div>
      {dialogs.dialog}
    </div>
  );
}

/**
 * Eine Fassung ohne Christine (K2, CUR-2291): der Grund aus dem Manifest und, als Hinweis,
 * der Stand der 60-s-Fassung derselben Folge (D25). Kein Schalter — die Ausnahme steht im
 * Repo, mit Commit und Ticket, nicht hinter einem Klick.
 */
function OhneChristine({ verzicht, langfassung }: { verzicht: string; langfassung: LangfassungsStand | null }): JSX.Element {
  return (
    <section className="mb-5 rounded-lg border p-4" style={{ borderColor: t.border, backgroundColor: t.bgSurface }} aria-label="Ohne Christine">
      <h2 className="text-lg font-semibold" style={{ color: t.textPrimary }}>Ohne Christine</h2>
      <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>{verzicht}</p>
      {langfassung && (
        <p className="mt-2 text-sm" style={{ color: langfassung.state === 'FREIGEGEBEN' ? t.success : t.textSecondary }}>
          {langfassung.state === 'FREIGEGEBEN' && <span aria-hidden>👍 </span>}
          {langfassungText(langfassung)}
          {langfassung.anmerkung && <span style={{ color: t.textSecondary }}> — Christine: {langfassung.anmerkung}</span>}
        </p>
      )}
      <p className="mt-2 text-xs" style={{ color: t.textTertiary }}>
        Diese Fassung geht nicht zur Prüfung. Die Pflichtpunkte gelten trotzdem
        {/* Die 60-s-Fassung selbst (D29) hat keine Langfassung, die sie nachsehen müsste. */}
        {langfassung ? ' — und ob Christine an der 60-s-Fassung etwas korrigiert hat, gehört vor dem Posten nachgesehen.' : '.'}
      </p>
    </section>
  );
}

/**
 * Der Ordner im Repo, ausgeschrieben und kopierbar (CUR-2287, Spec §16.4).
 *
 * Fehlt er, sagt das Cockpit es — leer gelassen sähe es aus wie „noch nicht
 * geladen". Bei Entwürfen lässt er sich hier eintragen und ändern (§16 O3 AC1,
 * CUR-2289); beim importierten Beitrag gilt das Manifest, dort gibt es keinen Knopf.
 * Er steht nicht im Fingerprint — ihn zu ändern hebt keine Freigabe auf.
 */
function Ordner({ ordner, ausManifest, aenderbar, speichert, speichern, verwerfen }: {
  ordner: string | null;
  ausManifest: boolean;
  aenderbar: boolean;
  speichert: boolean;
  speichern: (ordner: string | null) => Promise<unknown>;
  /** Abbrechen nimmt auch die Ablehnung des Servers mit — sie gehörte zur verworfenen Eingabe. */
  verwerfen: () => void;
}): JSX.Element {
  const [wert, setWert] = useState<string | null>(null);

  if (wert !== null) {
    const absenden = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        // Leer heißt: Ordner herausnehmen. Der Server prüft das Muster und sagt, was nicht passt.
        await speichern(wert.trim() || null);
        setWert(null);
      } catch {
        // Die Meldung steht schon oben im Cockpit; das Feld bleibt offen zum Korrigieren.
      }
    };
    return (
      <form className="mt-3" onSubmit={(e) => void absenden(e)}>
        <label className="block text-sm font-semibold" style={{ color: t.textPrimary }}>Ordner im Repo
          <input value={wert} onChange={(e) => setWert(e.target.value)} maxLength={300} autoFocus
            placeholder="E31 oder docs/marketing/…/E31-hormonsystem-diabetes"
            className="mt-2 w-full rounded-md border p-3 font-mono text-sm"
            style={{ backgroundColor: t.bgSurface, borderColor: t.border, color: t.textPrimary }} />
          <span className="mt-1 block text-xs font-normal" style={{ color: t.textSecondary }}>
            Ein Kürzel wie E31, S03, LC-01 oder der Pfad des Ordners. Leer lassen nimmt den Ordner heraus.
            Eine Freigabe bleibt davon unberührt.
          </span>
        </label>
        <div className="mt-2 flex gap-2">
          <button type="submit" disabled={speichert} className="min-h-[44px] rounded-lg px-4 text-sm font-semibold text-white"
            style={{ backgroundColor: t.accent, opacity: speichert ? 0.6 : 1 }}>
            Ordner speichern
          </button>
          <button type="button" onClick={() => { setWert(null); verwerfen(); }} className="min-h-[44px] rounded-lg px-4 text-sm" style={{ color: t.textTertiary }}>
            Abbrechen
          </button>
        </div>
      </form>
    );
  }

  const knopf = aenderbar && (
    <button type="button" onClick={() => setWert(ordner ?? '')} className="min-h-[44px] text-sm font-semibold" style={{ color: t.accentLight }}>
      {ordner ? 'Ordner ändern' : 'Ordner eintragen'}
    </button>
  );
  if (!ordner) {
    return (
      <p className="mt-2 flex flex-wrap items-center gap-x-3 text-sm" style={{ color: t.textTertiary }}>
        <span>Ordner fehlt — dieser Eintrag ist noch keinem Ordner im Repo zugeordnet.</span>
        {/* Ohne Knopf bliebe offen, warum man ihn hier nicht eintragen kann. */}
        {ausManifest && <span>Er folgt dem Manifest: dort ergänzen und neu einspielen.</span>}
        {knopf}
      </p>
    );
  }
  return (
    <div className="mt-3">
      <CopyField label="Ordner" value={ordner} mono />
      {knopf}
    </div>
  );
}

/**
 * Vorschau-Karte — kompakt, nicht groß.
 *
 * Das Thumbnail dient dem **Wiedererkennen**, nicht der Bildkontrolle: Wer das
 * Bild prüfen will, öffnet die Datei. 160 px reichen, um zu sehen, dass es das
 * richtige Video ist — und lassen Platz für die Fakten daneben, die man beim
 * Hochladen wirklich braucht.
 */
function Vorschau({ daten }: { daten: CockpitDaten }): JSX.Element {
  const a = daten.asset;
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-md border p-3 sm:flex-row"
      style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
      {a.thumbnailUrl ? (
        <img src={a.thumbnailUrl} alt="" className="h-[90px] w-[160px] shrink-0 rounded object-cover" />
      ) : (
        // Platzhalter mit Strang-Kürzel statt gebrochenem Bild — ein kaputtes
        // Bild-Icon liest sich als Fehler, ein Kürzel als „noch nicht synchronisiert".
        <div className="flex h-[90px] w-[160px] shrink-0 items-center justify-center rounded text-xs"
          style={{ backgroundColor: t.bgElevated, color: t.textTertiary }}>
          {a.stream.slice(0, 12)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium" style={{ color: t.textPrimary }}>{a.slug}</p>
        <p className="text-xs" style={{ color: t.textTertiary }}>
          {dauer(a.durationSec)} · {a.aspectRatio ?? '—'}
          {a.fileSizeMb !== null ? ` · ${a.fileSizeMb} MB` : ''}
        </p>
        <div className="mt-2">
          {a.videoPath && <CopyField label="Videopfad" value={a.videoPath} mono />}
        </div>
      </div>
    </div>
  );
}

function Einstellungen({ settings }: { settings: { label: string; value: string }[] }): JSX.Element {
  return (
    <section className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: t.textTertiary }}>
        Einstellungen der Upload-Maske
      </h2>
      <dl className="rounded-md border" style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
        {settings.map(({ label: k, value: val }, i) => {
          // Die Zeile zur KI-Offenlegung wird hervorgehoben — sie ist die eine,
          // die man nicht überfliegen darf.
          const heikel = /synthetisch|ki-/i.test(k) || /ANKREUZEN/.test(val);
          return (
            <div key={k} className="flex flex-wrap gap-2 px-3 py-2"
              style={{ borderTop: i === 0 ? 'none' : `1px solid ${t.borderSubtle}` }}>
              <dt className="w-full text-xs sm:w-56" style={{ color: t.textTertiary }}>{k}</dt>
              <dd className="flex-1 text-sm" style={{ color: heikel ? t.warning : t.textSecondary }}>
                {heikel ? '⚠ ' : ''}{val}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

/** Die Begründungsprosa aus dem Manifest — sie erklärt, warum das Paket so aussieht. */
function Notizen({ notes }: { notes: Record<string, string> }): JSX.Element {
  return (
    <section className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: t.textTertiary }}>
        Aus dem Manifest
      </h2>
      <div className="space-y-2 rounded-md border p-3" style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
        {Object.entries(notes).map(([k, val]) => (
          <p key={k} className="text-sm" style={{ color: t.textSecondary }}>
            <span className="font-semibold" style={{ color: t.textTertiary }}>{k}: </span>
            {val}
          </p>
        ))}
      </div>
    </section>
  );
}

function Pflichten({
  pflichten, gehakt, erledigt, offeneBlocker, umschalten, aenderbar,
}: {
  pflichten: Pruefpunkt[];
  gehakt: string[];
  erledigt: number;
  offeneBlocker: number;
  umschalten: (id: string) => void;
  /** Ohne Schreibrecht bleibt die Liste sichtbar, aber unveraenderlich — der
   *  Stand der Pflichten ist Information, nicht nur ein Bedienelement. */
  aenderbar: boolean;
}): JSX.Element {
  return (
    <section className="mb-4">
      <h2 className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide"
        style={{ color: t.textTertiary }}>
        <span>Pflichten</span>
        <span style={{ color: offeneBlocker > 0 ? t.warning : t.textTertiary }}>
          {erledigt} von {pflichten.length}
          {offeneBlocker > 0 ? ` · ${offeneBlocker} Pflicht${offeneBlocker === 1 ? '' : 'en'} offen` : ''}
        </span>
      </h2>
      <ul className="rounded-md border" style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
        {pflichten.map((p, i) => {
          const an = gehakt.includes(p.id);
          return (
            <li key={p.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${t.borderSubtle}` }}>
              {/* Echte Checkbox mit Label — keine Div-Attrappe, sonst fällt die
                  Tastaturbedienung weg. */}
              <label
                className={`flex min-h-[44px] items-start gap-3 px-3 py-2 ${
                  aenderbar ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <input
                  type="checkbox"
                  checked={an}
                  disabled={!aenderbar}
                  onChange={() => umschalten(p.id)}
                  className="mt-1"
                  {...(p.blocking ? { 'aria-describedby': `warum-${p.id}` } : {})}
                />
                <span className="flex-1 text-sm" style={{ color: an ? t.textTertiary : t.textSecondary }}>
                  {p.text}
                  {p.blocking && (
                    <span id={`warum-${p.id}`} className="ml-2 text-xs font-semibold" style={{ color: t.warning }}>
                      ⚠ blockierend
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Skelett(): JSX.Element {
  return (
    <div className="mx-auto max-w-[760px] space-y-3 p-6">
      {[64, 120, 40, 200, 40, 120].map((h, i) => (
        <div key={i} className="animate-pulse rounded-md" style={{ height: h, backgroundColor: t.bgSurface }} />
      ))}
    </div>
  );
}
